import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import {
  AirbnbDeclineBookingPayload,
  AirbnbNotificationItem,
  AirbnbOpportunityResponse,
  AirbnbUpdateSyncCategoryPayload,
} from './model'
import { HttpGetRequestCacheService } from '@tokeet-frontend/tv3-platform'
import { AirbnbHost2GuestReviewPayload, AirbnbHost2GuestReviewResponse, AirbnbReviewSubmittedResponse } from './review'
import { forkJoin, Observable } from 'rxjs'
import { AirbnbBookingSettingsResponse, AirbnbUpdateBookingSettingsPayload } from './booking_settings'
import { AirbnbCancelBookingPayload, CancellationByHostReasonsResponse } from './cancellations'
import { map } from 'rxjs/operators'
import { AirbnbCheckinType } from './checkin_type'
import {
  AirbnbReservationAlterationResponse,
  AirbnbReservationAlterationPriceQuotePayload,
  AirbnbReservationAlterationPriceQuoteResponse,
} from './alteration'
import { AirbnbDeactivationReasonFlowResponse } from './deactivation'
import { AirbnbListingQualityItem, AirbnbQualityStats, AirbnbRatingStats } from './quality'
import {
  AirbnbListingDiscountsResponse,
  AirbnbListingPromotionItem,
  AirbnbListingPromotionProgramStatus,
  AirbnbMobileOnlyDiscountStatus,
  AirbnbPromotionType,
  AirbnbPromotionTypeValue,
  AirbnbTopRatedGuestDiscountStatus,
} from './promotions'
import * as lodash from 'lodash'
import { AirbnbSpecialConsiderationsParams, AirbnbSpecialConsiderationsResponse } from './special_considerations'

@Injectable({
  providedIn: 'root',
})
export class AirbnbChannelService {
  constructor(private http: HttpClient, private httpCache: HttpGetRequestCacheService) {}

  getListingReviews(listingId: string | number, refresh = false) {
    // room id
    const url = `@api/abbv2/listing/reviews/${listingId}`
    return this.httpCache.cacheGetRequest<AirbnbReviewSubmittedResponse[]>(url, refresh)
  }

  getHostNotifications(hostId: string | number, refresh = false) {
    // property Id
    const url = `@api/abbv2/host/notifications/${hostId}`
    return this.httpCache.cacheGetRequest<AirbnbNotificationItem[]>(url, refresh)
  }

  getListingOpportunities(listingId: string | number, refresh = false) {
    // room Id
    const url = `@api/abbv2/host/opportunities/${listingId}`
    return this.httpCache.cacheGetRequest<AirbnbOpportunityResponse>(url, refresh)
  }

  getOpportunities(propertyId: any, listingId?: string | number | null, refresh = false) {
    let url = `@api/abbv2/host/opportunities?property_id=${propertyId}`
    if (listingId) {
      url += `&listing_id=${listingId}`
    }

    return this.httpCache
      .cacheGetRequest<{ data: AirbnbOpportunityResponse; next_cursor: string | null }>(url, refresh)
      .pipe(map(({ data }) => data))
  }

  setListingOpportunity(listingId: string | number, opportunityType: string, payload: any) {
    const url = `@api/abbv2/host/opportunities/${listingId}?type=${opportunityType}`
    return this.http.put<AirbnbOpportunityResponse>(url, payload)
  }

  setOpportunity(opportunityType: string, propertyId: string, payload: any) {
    const url = `@api/abbv2/host/opportunities?type=${opportunityType}&property_id=${propertyId}`
    return this.http.put<AirbnbOpportunityResponse>(url, payload)
  }

  acceptBooking(code: string) {
    const url = `@api/abbv2/reservation/${code}/accept`
    return this.http.put<any>(url, {})
  }

  declineBooking(code: string, payload: AirbnbDeclineBookingPayload) {
    const url = `@api/abbv2/reservation/${code}/deny`
    return this.http.put<any>(url, payload)
  }

  getBookingReview(confirmCode: string) {
    const url = `@api/abbv2/host/review/${confirmCode}`
    return this.http.get<AirbnbHost2GuestReviewResponse>(url)
  }

  getGuestReview(confirmCode: string) {
    const url = `@api/abbv2/guest/review/${confirmCode}`
    return this.http.get<AirbnbHost2GuestReviewResponse>(url)
  }

  setGuestReview(confirmCode: string, payload: AirbnbHost2GuestReviewPayload) {
    const url = `@api/abbv2/guest/review/${confirmCode}`
    return this.http.put<AirbnbReviewSubmittedResponse>(url, payload)
  }

  respondReview(listingId: string, reviewId: string, message: string) {
    const url = `@api/abbv2/listing/${listingId}/review/${reviewId}/respond`
    return this.http.put<any>(url, { reviewee_response: message })
  }

  getBookingSettingsV2(channelId: string, propertyId: any, roomId: any): Observable<AirbnbBookingSettingsResponse> {
    const url = `@api/abbv2/booking/settings/${propertyId}/${roomId}/${channelId}`
    return this.http.get<any>(url)
  }

  updateBookingSettingsV2(payload: AirbnbUpdateBookingSettingsPayload) {
    const url = `@api/abbv2/booking/settings`
    return this.http.put(url, payload)
  }

  getSpecialConsiderations(params: AirbnbSpecialConsiderationsParams) {
    return this.http
      .get<AirbnbSpecialConsiderationsResponse>('@api/abbv2/listing/special-considerations', { params: { ...params } })
      .pipe(map((response) => this.validateSpecialConsiderations(response, params.locale)))
  }

  updateSpecialConsiderations(params: AirbnbSpecialConsiderationsParams, special_considerations: string) {
    return this.http
      .put<AirbnbSpecialConsiderationsResponse>(
        '@api/abbv2/listing/special-considerations',
        { special_considerations },
        { params: { ...params } }
      )
      .pipe(
        map((response) => {
          this.validateSpecialConsiderations(response, params.locale)
          if (response.special_considerations !== special_considerations) {
            throw new Error($localize`Airbnb did not confirm the saved special considerations.`)
          }
          return response
        })
      )
  }

  private validateSpecialConsiderations(response: AirbnbSpecialConsiderationsResponse, locale: string) {
    if (response?.locale !== locale || typeof response.special_considerations !== 'string') {
      throw new Error($localize`Unable to read special considerations for the selected language.`)
    }
    return response
  }

  getListingSyncCategory(propertyId: any, roomId: any) {
    const url = `@api/abbv2/listing/detail?listing=${roomId}&property=${propertyId}&original_data=1&attributes=synchronization_category`
    return this.http.get<{ synchronization_category: string }>(url)
  }

  updateListingSyncCategory(payload: AirbnbUpdateSyncCategoryPayload) {
    const url = `@api/abbv2/sync/category`
    return this.http.put<{ synchronization_category: string }>(url, payload)
  }

  cancelBooking(code: string, payload: AirbnbCancelBookingPayload) {
    const url = `@api/abbv2/reservation/${code}/cancel`
    return this.http.put<any>(url, payload)
  }

  getCancellationReasons(code: string, cancelBy = 'cancellation_by_host') {
    const url = `@api/abbv2/reservation/${code}?attributes=${cancelBy}`
    return this.http.get<CancellationByHostReasonsResponse>(url)
  }

  getCheckinTypes() {
    const url = `@api/rental/checkin_types/all`
    return this.httpCache.cacheGetRequest<AirbnbCheckinType[]>(url)
  }

  getReservationAlterationById(alterationId: string) {
    const url = `@api/abbv3/reservation/host/alteration/${alterationId}`
    return this.http
      .get<{ reservation_alteration: AirbnbReservationAlterationResponse }>(url)
      .pipe(map(({ reservation_alteration }) => reservation_alteration))
  }

  updateReservationAlterationById(alterationId: string, action: 'ACCEPTED' | 'DECLINED' | 'CANCELED') {
    const url = `@api/abbv3/reservation/host/alteration/${alterationId}`
    return this.http.put(url, { status: action })
  }

  getAlterationPriceQuote(code: string, payload: AirbnbReservationAlterationPriceQuotePayload) {
    const url = `@api/abbv2/price/quote/reservation/${code}`
    return this.http.put<AirbnbReservationAlterationPriceQuoteResponse>(url, payload)
  }

  initAlteration(code: string, payload: AirbnbReservationAlterationPriceQuotePayload) {
    const url = `@api/abbv3/reservation/host/alteration/${code}`
    const { start_date, end_date, total_price_override, listing_id, ...guest_details } = payload
    return this.http.post<{ id: string }>(url, {
      confirmation_code: code,
      start_date,
      end_date,
      guest_details,
      total_price: total_price_override,
      listing_id,
    })
  }

  getPropertyDeactivationReasons(propertyId: any) {
    const url = `@api/abbv2/flow/${propertyId}/listing_deactivation`
    return this.http.get<{ flows: AirbnbDeactivationReasonFlowResponse[] }>(url).pipe(map((data) => data.flows[0]))
  }

  getListings(hotelId: number, has_recent_trip_issues?: boolean) {
    const url = `@api/abbv2/quality/listing/issues`
    const params: any = { property: hotelId }
    if (has_recent_trip_issues === true) {
      params.has_recent_trip_issues = 1
    }
    return this.http.get<AirbnbListingQualityItem[]>(url, { params })
  }

  getListingIssues(hotelId: number) {
    const url = `@api/abbv2/quality/listing/issues`
    return this.http.get<AirbnbListingQualityItem[]>(url, { params: { property: hotelId } })
  }

  getListingStats(hotelId: number, listings?: string[]) {
    const url = `@api/abbv2/quality/listing/stats`
    return this.http
      .put<{ quality_stats: AirbnbQualityStats[] }>(url, listings ? { listing_ids: listings } : {}, {
        params: { property: hotelId },
      })
      .pipe(map(({ quality_stats }) => quality_stats))
  }

  getListingRatings(hotelId: number, listings?: string[]): Observable<AirbnbRatingStats[]> {
    // max 50 listings per request
    if (listings && listings.length > 50) {
      return forkJoin(
        lodash.chunk(listings, 50).map((chunk) => {
          return this.getListingRatings(hotelId, chunk)
        })
      ).pipe(map((results) => results.flat()))
    }

    const url = `@api/abbv2/quality/listing/ratings`
    return this.http
      .put<{ rating_stats: AirbnbRatingStats[] }>(url, listings ? { listings } : {}, { params: { property: hotelId } })
      .pipe(map(({ rating_stats }) => rating_stats))
  }
  // Promotions, channelId from connection
  getListingPromotions(propertyId: any, channelId: string, listingIds: any[]) {
    const url = `@api/abbv2/promotions/${propertyId}?channel_id=${channelId}&${listingIds
      .map((id) => `listing_ids=${id}`)
      .join('&')}`
    return this.http.get<{ promotions: AirbnbListingPromotionItem[] }>(url).pipe(map((data) => data.promotions))
  }

  createListingPromotion(
    propertyId: any,
    channelId: string,
    listingIds: any[],
    promotionType: AirbnbPromotionTypeValue = AirbnbPromotionType
  ) {
    const url = `@api/abbv2/promotions/create/${propertyId}?channel_id=${channelId}`
    return this.http.post<{ success: boolean }>(url, { promotion_type: promotionType, listing_ids: listingIds })
  }

  deleteListingPromotion(
    propertyId: any,
    channelId: string,
    listingIds: any[],
    promotionType: AirbnbPromotionTypeValue = AirbnbPromotionType
  ) {
    const url = `@api/abbv2/promotions/delete/${propertyId}?channel_id=${channelId}`
    return this.http.post<{ success: boolean }>(url, { promotion_type: promotionType, listing_ids: listingIds })
  }

  getListingDiscounts(propertyId: any, channelId: string, listingId: any) {
    const url = `@api/abbv2/promotions/listing-discounts/${propertyId}?channel_id=${channelId}&listing_id=${listingId}`
    return this.http.get<AirbnbListingDiscountsResponse>(url)
  }

  setTopRatedGuestDiscount(propertyId: any, channelId: string, listingId: any, enabled: boolean) {
    const url = `@api/abbv2/promotions/top-rated-guests/${propertyId}`
    return this.http.put<AirbnbTopRatedGuestDiscountStatus>(url, {
      channelId,
      roomid: listingId,
      enabled,
    })
  }

  setMobileOnlyDiscount(propertyId: any, channelId: string, listingId: any, enabled: boolean) {
    const url = `@api/abbv2/promotions/mobile-only/${propertyId}`
    return this.http.put<AirbnbMobileOnlyDiscountStatus>(url, {
      channelId,
      roomid: listingId,
      enabled,
    })
  }

  setListingDiscountBlackoutDates(
    propertyId: any,
    channelId: string,
    listingId: any,
    type: AirbnbPromotionTypeValue,
    datesToAdd: string[],
    datesToRemove: string[]
  ) {
    const url = `@api/abbv2/promotions/blackout-dates/${propertyId}`
    return this.http.put<AirbnbListingPromotionProgramStatus>(url, {
      channelId,
      roomid: listingId,
      type,
      blackout_dates_to_add: datesToAdd,
      blackout_dates_to_remove: datesToRemove,
    })
  }
}
